import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  House, Megaphone, Image as ImageIcon, CalendarBlank,
  RocketLaunch, Star, ChartBar, EnvelopeSimple,
  Users as UsersIcon, SignOut, ArrowSquareOut, Article, CaretDown, CaretRight, Sparkle
} from "@phosphor-icons/react";
import { useAuth } from "./AuthContext";
import { LOGO_URL, BRAND } from "@/lib/api";

// Primary tabs the business uses day-to-day.
const primaryItems = [
  { to: "/admin", label: "Dashboard", icon: House, end: true },
  { to: "/admin/leads", label: "Leads", icon: UsersIcon },
  { to: "/admin/landing", label: "Landing Pages", icon: RocketLaunch },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
];

// Secondary / power-user tools. Social connections + per-platform analytics
// were removed in iter16 — content tools remain for drafting & exporting.
const moreItems = [
  { to: "/admin/marketing", label: "Marketing Tools", icon: Megaphone },
  { to: "/admin/journal", label: "Journal & Blog", icon: Article },
  { to: "/admin/creatives", label: "AI Creatives", icon: ImageIcon },
  { to: "/admin/calendar", label: "Content Calendar", icon: CalendarBlank },
  { to: "/admin/competitors", label: "Competitors", icon: ChartBar },
  { to: "/admin/email", label: "Email Campaigns", icon: EnvelopeSimple },
];

const STORAGE_KEY = "tnm_sidebar_more_open";

function NavRow({ it }) {
  const Icon = it.icon;
  return (
    <NavLink
      to={it.to}
      end={it.end}
      data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-sm border-l-2 ${
          isActive ? "bg-navy text-white border-navy font-semibold"
                   : "text-slate-300 hover:bg-slate-800 hover:text-white border-transparent"
        }`}>
      <Icon size={17} weight="duotone" />
      <span className="flex-1">{it.label}</span>
    </NavLink>
  );
}

export default function AdminSidebar() {
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; }
    catch (err) { console.warn("[sidebar] read storage failed:", err?.name || err); return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, moreOpen ? "1" : "0"); }
    catch (err) { console.warn("[sidebar] write storage failed:", err?.name || err); }
  }, [moreOpen]);

  return (
    <aside data-testid="admin-sidebar" className="hidden md:flex w-64 shrink-0 flex-col bg-navy-deep text-slate-200 min-h-screen sticky top-0">
      <div className="px-5 py-6 border-b border-slate-800 flex items-center gap-3">
        <img src={LOGO_URL} alt={BRAND.name} className="w-12 h-12 object-contain" />
        <div className="min-w-0">
          <div className="font-display font-black text-white tracking-tight text-sm leading-tight">TRUE NORTH<br/>MAINTENANCE</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-white/55 mt-1">Admin</div>
        </div>
      </div>

      <nav className="flex-1 py-3 px-2.5 space-y-0.5 overflow-y-auto scroll-thin">
        {primaryItems.map((it) => <NavRow key={it.to} it={it} />)}

        <button
          data-testid="nav-more-toggle"
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className="mt-3 w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm border-l-2 border-transparent transition-colors"
        >
          <Sparkle size={17} weight="duotone" />
          <span className="flex-1 text-left">More tools</span>
          {moreOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        </button>

        {moreOpen && (
          <div data-testid="nav-more-panel" className="space-y-0.5 pt-1 pl-2 border-l border-slate-800 ml-3 mr-0.5">
            {moreItems.map((it) => <NavRow key={it.to} it={it} />)}
          </div>
        )}
      </nav>

      <div className="border-t border-slate-800 p-3 space-y-2">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center justify-between px-2 py-1.5 text-xs text-slate-400 hover:text-white">
          <span>View website</span><ArrowSquareOut size={12} />
        </a>
        <div className="px-2 py-2 bg-slate-800/60 rounded-sm">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Signed in</div>
          <div className="text-xs text-white truncate mt-0.5">{user?.email}</div>
        </div>
        <button data-testid="btn-logout" onClick={logout} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm">
          <SignOut size={14} />Sign out
        </button>
      </div>
    </aside>
  );
}
